import { useState } from "react";
import { Cpu, UserCheck, ShieldCheck, Heart } from "lucide-react";

const businessFeatures = [
  {
    icon: Cpu,
    title: "Moderne Technologien",
    text: "Innovative Geräte und moderne Behandlungsmethoden.",
  },
  {
    icon: UserCheck,
    title: "Individuelle Behandlung",
    text: "Jede Behandlung wird auf Ihre persönlichen Bedürfnisse abgestimmt.",
  },
  {
    icon: ShieldCheck,
    title: "Qualität & Sicherheit",
    text: "Professionelle Durchführung mit hohen Ansprüchen an Hygiene und Sicherheit.",
  },
  {
    icon: Heart,
    title: "Persönliche Atmosphäre",
    text: "Keine Massenabfertigung – persönliche Betreuung und Zeit für jede Kundin.",
  },
];

export const AboutSection = ({ onBookingClick }) => {
  const [activeTab, setActiveTab] = useState("studio");

  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F9F8F6]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Image Column */}
          <div className="relative order-2 lg:order-1 lg:sticky lg:top-28">
            <div className="relative overflow-hidden">
              <img
                src={activeTab === "alexandra" ? "/alexandra.jpeg" : "https://customer-assets-lqy194kg.emergentagent.net/job_216d3c35-aa83-4452-b36d-63c5224739d4/artifacts/9k0un4ru_WhatsApp%20Image%202026-08-24%20at%2020.25.21s.jpeg"}
                alt="Urban Beauty Studio in Klosters – Alexandra Urban"
                className="w-full h-[500px] md:h-[600px] object-cover"
                data-testid="about-image"
              />
            </div>
            <div
              className="absolute -bottom-6 -right-6 w-32 h-32 border border-[#C5A059]"
              aria-hidden="true"
            />
          </div>

          {/* Content Column */}
          <div className="order-1 lg:order-2 lg:pl-8">
            <p
              className="text-[#C5A059] text-sm tracking-[0.3em] uppercase mb-6"
              data-testid="about-tagline"
            >
              Über uns
            </p>

            {/* Sub-tabs */}
            <div className="flex gap-3 mb-8" data-testid="about-subtabs">
              {[
                { id: "studio", label: "Das Studio" },
                { id: "alexandra", label: "Alexandra" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`about-tab-${tab.id}`}
                  className={`px-5 py-2.5 rounded-full text-sm tracking-wide transition-all duration-300 ${
                    activeTab === tab.id
                      ? "bg-[#1A1A1A] text-white"
                      : "border border-[#E5E5E5] text-[#1A1A1A] hover:border-[#C5A059]"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Studio / Business */}
            {activeTab === "studio" ? (
              <div data-testid="about-content-studio">
                <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] leading-tight mb-4">
                  Über <span className="italic">Urban Beauty Klosters</span>
                </h2>
                <p className="font-cormorant italic text-xl md:text-2xl text-[#3d7d83] mb-6">
                  Moderne Beauty. Persönliche Betreuung. Sichtbare Ergebnisse.
                </p>
                <div className="w-16 h-[1px] bg-gradient-to-r from-[#C5A059] to-[#3d7d83] mb-8" />

                <div className="space-y-5 text-[#666666] leading-relaxed">
                  <p>
                    Urban Beauty Klosters ist ein modernes Beauty-Institut, in dem
                    innovative Technologien, professionelle Kosmetik und
                    persönliche Betreuung miteinander verbunden werden.
                  </p>
                  <p>
                    Im Mittelpunkt steht nicht eine Behandlung „von der Stange“,
                    sondern ein individuell abgestimmtes Konzept. Jede Haut, jeder
                    Körper und jedes persönliche Ziel ist anders – deshalb beginnt
                    jede Behandlung mit einer persönlichen Einschätzung und wird
                    gezielt auf die individuellen Bedürfnisse abgestimmt.
                  </p>
                  <p>
                    Das Angebot reicht von dauerhafter Haarentfernung mit moderner
                    Lasertechnologie über Laser-Hautverjüngung und Hautstraffung
                    bis hin zu Futura Contour und ausgewählten kosmetischen
                    Gesichtsbehandlungen.
                  </p>
                  <p>
                    Dabei kommen moderne, professionelle Geräte und sorgfältig
                    ausgewählte Behandlungsmethoden zum Einsatz, mit dem Ziel, die
                    natürliche Schönheit zu unterstützen, das Hautbild sichtbar zu
                    verbessern und langfristig gepflegte Ergebnisse zu erzielen.
                  </p>
                  <p>
                    Urban Beauty Klosters steht für eine ruhige, persönliche
                    Atmosphäre, in der Zeit für Beratung und individuelle Betreuung
                    bleibt. Diskretion, Hygiene, Sicherheit und Qualität haben
                    dabei höchste Priorität.
                  </p>
                  <p className="text-[#1A1A1A]">
                    Ein Ort für moderne Beauty-Behandlungen – persönlich,
                    professionell und mit viel Liebe zum Detail.
                  </p>
                </div>

                {/* Feature grid */}
                <div className="grid sm:grid-cols-2 gap-6 mt-10">
                  {businessFeatures.map((f) => (
                    <div
                      key={f.title}
                      className="flex gap-4"
                      data-testid={`about-feature-${f.title.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}
                    >
                      <div className="shrink-0 w-11 h-11 rounded-full bg-white border border-[#E5E5E5] flex items-center justify-center text-[#3d7d83]">
                        <f.icon size={20} />
                      </div>
                      <div>
                        <p className="text-xs tracking-[0.15em] uppercase text-[#1A1A1A] font-medium mb-1">
                          {f.title}
                        </p>
                        <p className="text-sm text-[#666666] leading-relaxed">
                          {f.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* Alexandra */
              <div data-testid="about-content-alexandra">
                <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] leading-tight mb-4">
                  Über mich – <span className="italic">Alexandra Urban</span>
                </h2>
                <p className="font-cormorant italic text-xl md:text-2xl text-[#3d7d83] mb-6">
                  Schönheit bedeutet für mich, sich in der eigenen Haut
                  wohlzufühlen.
                </p>
                <div className="w-16 h-[1px] bg-gradient-to-r from-[#C5A059] to-[#3d7d83] mb-8" />

                <div className="space-y-5 text-[#666666] leading-relaxed">
                  <p>
                    Mein Name ist Alexandra Urban, ich bin Kosmetikerin und
                    Inhaberin von Urban Beauty Klosters.
                  </p>
                  <p>
                    Ich weiss, dass hinter dem Wunsch nach einer Beauty-Behandlung
                    oft viel mehr steckt als nur der Wunsch, etwas am Äusseren zu
                    verändern. Manchmal möchte man sich wieder frischer fühlen,
                    sich wohler im eigenen Körper fühlen oder einfach etwas für
                    sich selbst tun. Genau deshalb ist mir der persönliche Kontakt
                    zu meinen Kundinnen so wichtig.
                  </p>
                  <p>
                    Ich möchte verstehen, was Sie stört, was Sie sich wünschen und
                    welches Ergebnis Sie sich erhoffen. Nicht jede Behandlung passt
                    zu jeder Haut und nicht jede Kundin braucht dasselbe. Deshalb
                    nehme ich mir Zeit für eine ehrliche Beratung und empfehle nur
                    das, was ich persönlich für sinnvoll und passend halte.
                  </p>
                  <p>
                    Natürlich gehört für mich dazu auch eine fundierte fachliche
                    Ausbildung. Ich bilde mich regelmässig weiter und verfüge über
                    die entsprechenden Sachkundenachweise gemäss V-NISSG für
                    Haarentfernung mit Laser sowie Haut und Pigmentierung. Moderne
                    Technologien faszinieren mich – aber ein gutes Gerät allein
                    macht für mich noch keine gute Behandlung aus. Entscheidend
                    sind Fachwissen, Erfahrung, die richtige Anwendung und der
                    verantwortungsvolle Umgang mit jeder einzelnen Kundin.
                  </p>
                  <p>
                    Bei Urban Beauty Klosters möchte ich einen Ort schaffen, an dem
                    Sie sich nicht beurteilt oder unter Druck gesetzt, sondern
                    verstanden und gut aufgehoben fühlen. Einen Ort, an dem Sie
                    Fragen stellen können, ehrlich beraten werden und wissen, dass
                    Ihre Wünsche ernst genommen werden.
                  </p>
                  <p>
                    Ich glaube nicht an Perfektion und ich möchte niemanden
                    verändern. Ich möchte Ihnen dabei helfen, das Beste aus Ihrer
                    eigenen, natürlichen Schönheit herauszuholen – damit Sie in den
                    Spiegel schauen und sich einfach ein bisschen wohler mit sich
                    selbst fühlen.
                  </p>
                  <p className="text-[#1A1A1A]">
                    Ich freue mich darauf, Sie persönlich kennenzulernen.
                  </p>
                </div>

                <div className="mt-8">
                  <p className="font-cormorant italic text-2xl text-[#1A1A1A]">
                    Alexandra Urban
                  </p>
                  <p className="text-sm text-[#666666] mt-1">
                    Kosmetikerin & Inhaberin von Urban Beauty Klosters
                  </p>
                </div>
              </div>
            )}

            {/* CTA */}
            <button
              onClick={onBookingClick}
              data-testid="about-cta-btn"
              className="btn-primary mt-8"
            >
              Kostenloses Beratungsgespräch
            </button>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-8 mt-12 pt-8 border-t border-[#E5E5E5]">
              <div data-testid="about-stat-1">
                <p className="font-playfair text-3xl md:text-4xl text-[#1A1A1A]">5.0</p>
                <p className="text-sm text-[#666666] mt-1">Bewertung</p>
              </div>
              <div data-testid="about-stat-3">
                <p className="font-playfair text-3xl md:text-4xl text-[#1A1A1A]">100%</p>
                <p className="text-sm text-[#666666] mt-1">Zufriedenheit</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
